import { useContext } from 'react';
import useLanguage from './useLanguage';
import Context from '../../context';

export default function useCurrency() {
  const { currencies, user } = useContext(Context);
  const userCurrency =
    user &&
    user.profile &&
    user.profile.settings &&
    user.profile.settings.currency
      ? user.profile.settings.currency
      : 'SGD';
  const { getText } = useLanguage();

  function getCurrent() {
    if (user && user.profile.settings && user.profile.settings.currency) {
      return user.profile.settings.currency;
    }
    if (userCurrency) {
      return userCurrency;
    }
    return null;
  }

  function getDetails(code: string) {
    if (code) {
      return currencies.find(
        (currency) => currency._id === code || currency.iso3 === code
      );
    }
    return null;
  }

  function format(rate: number = 0, code: string) {
    if (!code) {
      return rate;
    }
    const cu = getDetails(code);
    let tempFormat;

    if (cu) {
      tempFormat = rate >= 0 ? cu.format_positive : cu.format_negative;
      return tempFormat
        .replace(
          new RegExp('{number}', 'gi'),
          Number(rate).toFixed(cu.decimal_place)
        )
        .replace(new RegExp('{symbol}', 'gi'), cu.symbol)
        .replace(new RegExp('{sign}', 'gi'), '-');
    }
    return '';
  }

  function exchange(rate: string | number = 0, from: string, to?: string) {
    let thisFrom;
    let thisTo;

    let tempRate;
    if (typeof rate === 'string') {
      tempRate = parseFloat(rate);
    } else {
      tempRate = rate;
    }
    from = String(from);

    to = to || userCurrency || 'SGD';
    if (from) {
      thisFrom = getDetails(from);
    }
    if (to) {
      thisTo = getDetails(to);
    }
    if (tempRate && thisFrom && thisTo) {
      const thisRate = Math.round(tempRate * 100) / 100;
      const fromRate = parseFloat((thisFrom.rate as unknown) as string);
      const toRate = parseFloat((thisTo.rate as unknown) as string);
      const newRate = thisFrom.base
        ? fromRate * toRate
        : (1 / fromRate) * toRate;
      const lastRate = thisRate * newRate;

      return format(lastRate, thisTo.iso3);
    }
    if (!tempRate && thisFrom && thisTo) {
      return format(0, thisTo.iso3);
    }

    return '';
  }

  function exchangeWithoutFormat(
    rate: string | number = 0,
    from: string,
    to?: string
  ) {
    let thisFrom;
    let thisTo;

    let tempRate;
    if (typeof rate === 'string') {
      tempRate = parseFloat(rate);
    } else {
      tempRate = rate;
    }
    from = String(from);

    to = to || userCurrency || 'SGD';
    if (from) {
      thisFrom = getDetails(from);
    }
    if (to) {
      thisTo = getDetails(to);
    }
    if (tempRate && thisFrom && thisTo) {
      const thisRate = Math.round(tempRate * 100) / 100;
      const fromRate = parseFloat((thisFrom.rate as unknown) as string);
      const toRate = parseFloat((thisTo.rate as unknown) as string);
      const newRate = thisFrom.base
        ? fromRate * toRate
        : (1 / fromRate) * toRate;

      return Number(
        thisTo
          ? (thisRate * newRate).toFixed(thisTo.decimal_place)
          : (thisRate * newRate).toFixed(2)
      );
    }
    if (!tempRate && thisFrom && thisTo) {
      return 0;
    }

    return 0;
  }

  function exchangeRate(from: string, to?: string) {
    to = to || userCurrency;
    const thisFrom = getDetails(from);
    const thisTo = getDetails(to);

    if (thisFrom && thisTo) {
      const fromRate = parseFloat((thisFrom.rate as unknown) as string);
      const toRate = parseFloat((thisTo.rate as unknown) as string);
      const newRate = thisFrom.base
        ? fromRate * toRate
        : (1 / fromRate) * toRate;

      return newRate.toFixed(thisTo.decimal_place);
    }

    return null;
  }

  function getBase() {
    return currencies.find((currency) => currency.base);
  }

  function getAll() {
    return currencies.filter((currency) => currency.status);
  }

  function getName(code: string) {
    if (code) {
      const cu = getDetails(code);

      return cu ? getText(cu.name) : code;
    }
    return null;
  }

  function getSymbol(code: string) {
    if (code) {
      const cu = getDetails(code);

      return cu ? cu.symbol : '$';
    }
    return null;
  }

  return {
    getCurrent,
    format,
    exchange,
    exchangeWithoutFormat,
    exchangeRate,
    getBase,
    getAll,
    getDetails,
    getName,
    getSymbol,
    currency: userCurrency,
  };
}
